import { Alert, Box, Card, Tab, Tabs } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { DATE_FORMAT_INTERNATIONAL } from '../../../../constants/global'
import PageUrls from '../../../../constants/page-urls'
import { EPLATFORM } from '../../../../constants/platform'
import useAxios from '../../../../hooks/useAxios'
import { IShopeeAccount } from '../../../../types/shopee'
import StockDeductionTab from '../../components/StockDeduction/StockDeductionTab'
import BackfillButton from './components/BackfillButton'
import ByStatusTable from './components/ByStatusTable'
import DailyTable from './components/DailyTable'
import ReportFilter from './components/ReportFilter'
import SummaryCards from './components/SummaryCards'
import { IDailyReport, IOrderReport, IReportFilter } from './types'

const defaultFilter: IReportFilter = {
  startDate: dayjs().subtract(30, 'day').format(DATE_FORMAT_INTERNATIONAL),
  endDate: dayjs().format(DATE_FORMAT_INTERNATIONAL),
}

const ShopeeSalesReportPage = () => {
  const [filter, setFilter] = useState<IReportFilter>(defaultFilter)
  const [searchFilter, setSearchFilter] = useState<IReportFilter>(defaultFilter)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [activeTab, setActiveTab] = useState<number>(0)

  const { data: shopeeAccounts } = useAxios<IShopeeAccount[]>({
    method: 'GET',
    url: '/shopee/merchant/accounts',
  })

  const onFilterChange = useCallback((newFilter: IReportFilter) => {
    setFilter(newFilter)
  }, [])

  const handleSearch = (newFilter: IReportFilter) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
  }

  const params: IReportFilter = {
    startDate: searchFilter.startDate,
    endDate: searchFilter.endDate,
    ...(searchFilter.shopId ? { shopId: searchFilter.shopId } : {}),
  }

  const report = useAxios<IOrderReport>({
    method: 'GET',
    url: '/shopee/report/orders',
    params,
  })

  const daily = useAxios<IDailyReport>({
    method: 'GET',
    url: '/shopee/report/orders-daily',
    params,
  })

  // ---- Tab 1: แยกตามสถานะ ----
  const statusOptions = useMemo(
    () =>
      (report.data?.byStatus ?? [])
        .map((bucket) => bucket.status)
        .filter((status): status is string => !!status),
    [report.data],
  )

  const displayedSummary = useMemo(
    () =>
      statusFilter
        ? report.data?.byStatus?.find(
            (bucket) => bucket.status === statusFilter,
          )
        : report.data?.summary,
    [statusFilter, report.data],
  )

  const displayedRows = useMemo(() => {
    const rows = report.data?.byStatus ?? []
    return statusFilter
      ? rows.filter((bucket) => bucket.status === statusFilter)
      : rows
  }, [statusFilter, report.data])

  // ---- Tab 2: รายวัน ----
  const dailyRows = useMemo(
    () =>
      [...(daily.data?.records ?? [])].sort((a, b) =>
        a.date > b.date ? 1 : -1,
      ),
    [daily.data],
  )

  return (
    <ContentWrapper>
      <Helmet title="รายงานยอดขาย Shopee" />
      <HeaderContent
        goBack
        title="รายงานยอดขาย Shopee"
        descriptions="รายงานยอดขายจาก Shopee"
        childrenRight={<BackfillButton />}
      />
      <Card sx={{ padding: 2 }}>
        <ReportFilter
          shopeeAccounts={shopeeAccounts ?? []}
          filter={filter}
          statusFilter={statusFilter}
          statusOptions={statusOptions}
          showStatus={activeTab === 0}
          onFilterChange={onFilterChange}
          onStatusChange={setStatusFilter}
          onSearch={handleSearch}
        />

        <Tabs
          value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
          sx={{ mb: 2 }}
        >
          <Tab label="รายงานยอดขายแยกตามสถานะ" />
          <Tab label="รายงานยอดขายรายวัน" />
          <Tab label="รายงานตัดสต๊อก" />
        </Tabs>

        {report.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            ไม่สามารถโหลดรายงานได้ กรุณาลองใหม่อีกครั้ง
          </Alert>
        )}

        {(report.data?.summary?.costMissingCount ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            ต้นทุนสินค้าไม่ครบ {report.data?.summary?.costMissingCount} รายการ —
            บางสินค้ายังไม่ได้ผูก SKU ของ Shopee กับสินค้าในระบบ
            ยอดต้นทุน/กำไรขั้นต้นจึงต่ำกว่าความเป็นจริง
          </Alert>
        )}

        <Box hidden={activeTab !== 0}>
          <SummaryCards summary={displayedSummary} />
          <ByStatusTable data={displayedRows} isLoading={report.isLoading} />
        </Box>

        <Box hidden={activeTab !== 1}>
          <DailyTable data={dailyRows} isLoading={daily.isLoading} />
        </Box>

        {/* mount เมื่อเปิดแท็บเท่านั้น — จะได้ไม่ยิงรายงานตัดสต๊อกทิ้งทุกครั้งที่เข้าหน้า */}
        {activeTab === 2 && (
          <StockDeductionTab
            platform={EPLATFORM.SHOPEE}
            filter={searchFilter}
            detailUrl={PageUrls.Platforms.ShopeeStockReportDetail}
          />
        )}
      </Card>
    </ContentWrapper>
  )
}

export default ShopeeSalesReportPage
